/**
 * Settings Overview Page - Einstellungen Übersicht
 * Zentrale Übersicht für alle Einstellungen
 */

import React from "react";
import {
  Container,
  Paper,
  Typography,
  Box,
  Stack,
  Grid,
  Card,
  CardContent,
  CardActions,
  Button,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import {
  Settings as SettingsIcon,
  Business as BusinessIcon,
  People as PeopleIcon,
  Euro as EuroIcon,
  BusinessCenter as BusinessCenterIcon,
  Computer as ComputerIcon,
  Person as PersonIcon,
  VpnKey as ApiKeyIcon,
  SmartToy as SmartToyIcon,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { UserRole } from "../types/api-types";

interface SettingsSection {
  title: string;
  description: string;
  path: string;
  icon: React.ReactElement;
  color: string;
  requiredRoles?: UserRole[];
}

const SettingsPage: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === UserRole.ADMIN;

  const settingsSections: SettingsSection[] = [
    {
      title: "Profil",
      description: "Eigene Kontaktdaten und Passwort verwalten",
      path: "/profile",
      icon: <PersonIcon sx={{ fontSize: 40, color: "primary.main" }} />,
      color: "primary.main",
    },
    {
      title: "🏢 Unternehmen",
      description: "Firmendaten, Adresse und Bankverbindung verwalten",
      path: "/settings/company",
      icon: <BusinessIcon sx={{ fontSize: 40, color: "primary.main" }} />,
      color: "primary.main",
    },
    {
      title: "👥 Benutzer",
      description: "Benutzerkonten und Berechtigungen verwalten",
      path: "/settings/users",
      icon: <PeopleIcon sx={{ fontSize: 40, color: "secondary.main" }} />,
      color: "secondary.main",
      requiredRoles: [UserRole.ADMIN, UserRole.MANAGER],
    },
    {
      title: "💰 Preise",
      description: "Preisstrukturen und Objektpreise konfigurieren",
      path: "/settings/pricing",
      icon: <EuroIcon sx={{ fontSize: 40, color: "success.main" }} />,
      color: "success.main",
    },
    {
      title: "📊 Geschäftsstatus",
      description: "Geschäftsöffnungszeiten und Status verwalten",
      path: "/settings/business",
      icon: <BusinessCenterIcon sx={{ fontSize: 40, color: "warning.main" }} />,
      color: "warning.main",
    },
    {
      title: "� API-Schlüssel",
      description: "API-Schlüssel für externen Zugriff verwalten",
      path: "/settings/api-keys",
      icon: <ApiKeyIcon sx={{ fontSize: 40, color: "error.main" }} />,
      color: "error.main",
      requiredRoles: [UserRole.ADMIN],
    },
    {
      title: "KI",
      description: "KI-Prüfungen für Chat und Bildauswertung verwalten",
      path: "/settings/ai",
      icon: <SmartToyIcon sx={{ fontSize: 40, color: "info.main" }} />,
      color: "info.main",
      requiredRoles: [UserRole.ADMIN],
    },
    {
      title: "�🔧 System",
      description: "Systemeinstellungen und -überwachung",
      path: "/settings/system",
      icon: <ComputerIcon sx={{ fontSize: 40, color: "info.main" }} />,
      color: "info.main",
      requiredRoles: [UserRole.ADMIN],
    },
  ];
  const visibleSettingsSections = settingsSections.filter(
    (section) =>
      !section.requiredRoles ||
      (user?.role && section.requiredRoles.includes(user.role))
  );

  return (
    <Container
      maxWidth="xl"
      sx={{
        py: { xs: 1, md: 3 },
        px: { xs: 0.5, md: 3 },
        maxWidth: { xs: "100%", md: "xl" },
        overflow: "hidden",
      }}
    >
      {/* Header */}
      <Paper
        elevation={0}
        sx={{
          p: { xs: 1.5, md: 3 },
          mb: 3,
          background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
          color: "white",
          borderRadius: 2,
        }}
      >
        <Stack
          direction={{ xs: "column", sm: "row" }}
          alignItems={{ xs: "flex-start", sm: "center" }}
          justifyContent="space-between"
          spacing={2}
        >
          <Box>
            <Typography
              variant={isMobile ? "h5" : "h4"}
              component="h1"
              gutterBottom
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                fontWeight: "bold",
                mb: 1,
              }}
            >
              <SettingsIcon fontSize={isMobile ? "medium" : "large"} />
              Einstellungen
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.9 }}>
              Verwalten Sie alle Aspekte Ihres Mietwerkstatt-Systems
            </Typography>
          </Box>
        </Stack>
      </Paper>

      {/* Settings Overview */}
      <Grid container spacing={3}>
        {visibleSettingsSections.map((section, index) => (
          <Grid item xs={12} sm={6} lg={4} key={index}>
            <Card
              sx={{
                height: "100%",
                borderRadius: 2,
                boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
                transition: "all 0.3s ease",
                cursor: "pointer",
                "&:hover": {
                  transform: { xs: "none", md: "translateY(-4px)" },
                  boxShadow: "0 8px 30px rgba(0,0,0,0.15)",
                },
              }}
              onClick={() => navigate(section.path)}
            >
              <CardContent
                sx={{
                  p: { xs: 2, md: 3 },
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  textAlign: "center",
                  flexGrow: 1,
                }}
              >
                <Box sx={{ mb: 2 }}>{section.icon}</Box>

                <Typography
                  variant="h6"
                  gutterBottom
                  fontWeight="bold"
                  sx={{ color: section.color }}
                >
                  {section.title}
                </Typography>

                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ flexGrow: 1, mb: 2 }}
                >
                  {section.description}
                </Typography>
              </CardContent>

              <CardActions
                sx={{ p: { xs: 2, md: 3 }, pt: 0, justifyContent: "center" }}
              >
                <Button
                  variant="contained"
                  size="small"
                  sx={{
                    borderRadius: 2,
                    px: 3,
                    backgroundColor: section.color,
                    "&:hover": {
                      backgroundColor: section.color,
                      opacity: 0.9,
                    },
                  }}
                >
                  Verwalten
                </Button>
              </CardActions>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Quick Actions */}
      <Paper
        sx={{
          p: { xs: 2, md: 3 },
          mt: 4,
          borderRadius: 2,
          background: "linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)",
        }}
      >
        <Typography variant="h6" gutterBottom fontWeight="bold" color="primary">
          🔧 Schnellaktionen
        </Typography>

        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={2.4}>
            <Button
              fullWidth
              variant="outlined"
              onClick={() => navigate("/profile")}
              sx={{ borderRadius: 2, py: 1.5 }}
            >
              Profil bearbeiten
            </Button>
          </Grid>

          <Grid item xs={12} sm={6} md={2.4}>
            <Button
              fullWidth
              variant="outlined"
              onClick={() => navigate("/settings/company")}
              sx={{ borderRadius: 2, py: 1.5 }}
            >
              Firma bearbeiten
            </Button>
          </Grid>

          <Grid item xs={12} sm={6} md={2.4}>
            <Button
              fullWidth
              variant="outlined"
              onClick={() => navigate("/settings/users")}
              sx={{ borderRadius: 2, py: 1.5 }}
            >
              Benutzer hinzufügen
            </Button>
          </Grid>

          <Grid item xs={12} sm={6} md={2.4}>
            <Button
              fullWidth
              variant="outlined"
              onClick={() => navigate("/settings/pricing")}
              sx={{ borderRadius: 2, py: 1.5 }}
            >
              Preise anpassen
            </Button>
          </Grid>

          {isAdmin && (
            <Grid item xs={12} sm={6} md={2.4}>
              <Button
                fullWidth
                variant="outlined"
                onClick={() => navigate("/settings/api-keys")}
                sx={{ borderRadius: 2, py: 1.5 }}
              >
                API-Schlüssel
              </Button>
            </Grid>
          )}

          {isAdmin && (
            <Grid item xs={12} sm={6} md={2.4}>
              <Button
                fullWidth
                variant="outlined"
                onClick={() => navigate("/settings/system")}
                sx={{ borderRadius: 2, py: 1.5 }}
              >
                Systemstatus prüfen
              </Button>
            </Grid>
          )}
        </Grid>
      </Paper>
    </Container>
  );
};

export default SettingsPage;
